import React, { useState } from 'react';
import useSWR from 'swr';
import { useDispatch } from 'react-redux';
import {
  fetchAvailableRedemptionsSWR,
  getAvailableRedemptionsUrl,
  handleRedemption,
} from './actions';
import RewardItem from './RewardItem';
import './rewards-list.css';

const RewardsList = ({
  userId,
  storeId,
  restaurantId,
  level,
  themeColors,
  userPoints,
  pointsTerminology = 'points',
  sortBy = 'points_asc',
}) => {
  const dispatch = useDispatch();

  // Loading state for redemption
  const [redeemingId, setRedeemingId] = useState(null);

  // Get the URL for SWR to fetch available redemptions (without backend sorting)
  const redemptionsKey = getAvailableRedemptionsUrl(
    userId,
    storeId,
    restaurantId,
    level || 0
  );

  // Use SWR to fetch available redemptions
  const {
    data: availableRedemptions,
    error: availableRedemptionsError,
    isLoading: isLoadingAvailableRedemptions,
  } = useSWR(redemptionsKey, fetchAvailableRedemptionsSWR, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 10000, // dedupe requests within 10 seconds
  });

  // Client-side sorting
  const sortedRedemptions = React.useMemo(() => {
    if (!availableRedemptions || !Array.isArray(availableRedemptions)) {
      return [];
    }

    const sorted = [...availableRedemptions].sort((a, b) => {
      if (sortBy === 'points_asc') {
        // Sort by points ascending (lowest first)
        const pointsA = a.pointsRequired || a.points || 0;
        const pointsB = b.pointsRequired || b.points || 0;
        return pointsA - pointsB;
      } else if (sortBy === 'name_asc') {
        // Sort by name ascending (A-Z)
        const nameA = (a.name || a.title || '').toLowerCase();
        const nameB = (b.name || b.title || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    return sorted;
  }, [availableRedemptions, sortBy]);

  // Handle redemption with parameters as a single object
  const handleRedeemReward = async (redemptionId) => {
    try {
      // Set loading state for this specific redemption
      setRedeemingId(redemptionId);

      const result = await handleRedemption({
        redemptionId,
        userId,
        storeId,
        restaurantId,
        level: level || 0,
        dispatch, // Pass the dispatch function to handleRedemption
      });

      // Success handling is now done in the handleRedemption function
    } catch (error) {
      console.error('Error redeeming reward:', error);
      // Could display an error message to the user here
    } finally {
      // Clear loading state
      setRedeemingId(null);
    }
  };

  // Display loading state
  if (isLoadingAvailableRedemptions) {
    return (
      <div className='rewards-list-loading'>
        <div className='reward-placeholder'></div>
      </div>
    );
  }

  // Display error state
  if (availableRedemptionsError) {
    return (
      <div className='rewards-list-error'>
        Unable to load rewards. Please try again later.
      </div>
    );
  }

  // Display empty state if no redemptions available
  if (
    !isLoadingAvailableRedemptions &&
    (!sortedRedemptions || sortedRedemptions.length === 0)
  ) {
    return (
      <div className='rewards-list-empty'>
        <p>No rewards available at this time.</p>
      </div>
    );
  }

  return (
    <div className='rewards-list'>
      {sortedRedemptions && sortedRedemptions.length > 0 ? (
        sortedRedemptions.map((redemption) => (
          <RewardItem
            key={redemption.id}
            title={redemption.name}
            description={redemption.description}
            points={redemption.points}
            imageUrl={redemption.imageUrl}
            canRedeem={userPoints >= redemption.points && redeemingId === null}
            isLoading={redeemingId === redemption.id}
            isDisabled={redeemingId !== null && redeemingId !== redemption.id}
            onRedeem={() => handleRedeemReward(redemption.id)}
            themeColors={themeColors}
            pointsTerminology={pointsTerminology}
          />
        ))
      ) : (
        <div>No rewards available</div>
      )}
    </div>
  );
};

export default RewardsList;
